/**
 * Built-in IAssetKindProvider implementations: 8 core kinds + preset (9 total).
 * Flow + knowledge moved to their owning runtime packages in 1.7.3/1.7.4.
 * The standalone `mount` provider was removed in the connectors-unification
 * hard cut — mountable resources are now `connector` assets (CONNECTOR.md).
 *
 * Core kinds: skill, agent, connector, mcp-server, contract,
 *             prompt, persona, ruleset
 *
 * Each provider is self-contained: discovery patterns, path matching,
 * manifest validation, file filtering, and requires extraction are all
 * owned by the provider. This is the post-Phase-1.7 architecture where
 * IAssetKindRegistry is the single source of truth for kind-aware logic.
 */

import * as fs from "node:fs";
import * as path from "node:path";
import type { AssetKindFileFilter, IAssetKindProvider } from "@skaile/workspaces/plugins";
import type { ManifestValidationResult } from "@skaile/workspaces/types/manifests";
import {
  validateAgent,
  validateConnector,
  validateContract,
  validateMcpServer,
  validatePersona,
  validatePreset,
  validatePrompt,
  validateRuleset,
  validateSkill,
} from "@skaile/workspaces/types/manifests";

import { AGENT_COMPANION_DIRS, SKILL_COMPANION_DIRS } from "./atomization-conventions.js";
import { extractRequires as extractRequiresFromManifest } from "./requires-graph.js";

// ---------------------------------------------------------------------------
// Helper: path matching from filename
// ---------------------------------------------------------------------------

function matchesSuffix(relativePath: string, suffix: string): boolean {
  return relativePath.endsWith(suffix);
}

function matchesFilename(relativePath: string, filename: string): boolean {
  const parts = relativePath.split("/");
  return parts[parts.length - 1] === filename;
}

// ---------------------------------------------------------------------------
// File filter helpers (per-kind default file filters)
// ---------------------------------------------------------------------------

/**
 * Recursively collect all files under a subdirectory.
 * Uses path.posix.join for relative paths so SHA256 hashes are consistent
 * across platforms (Linux/macOS/Windows).
 */
function collectDirRecursive(baseDir: string, relativeDir: string): string[] {
  const full = path.join(baseDir, relativeDir);
  if (!fs.existsSync(full) || !fs.statSync(full).isDirectory()) return [];
  const results: string[] = [];
  for (const entry of fs.readdirSync(full, { withFileTypes: true })) {
    const rel = path.posix.join(relativeDir, entry.name);
    if (entry.isDirectory()) {
      results.push(...collectDirRecursive(baseDir, rel));
    } else if (entry.isFile()) {
      results.push(rel);
    }
  }
  return results;
}

/**
 * Manifest-only filter — for simple kinds that include only the manifest file.
 */
function manifestOnlyFilter(manifestPath: string): AssetKindFileFilter {
  return {
    assetRoot: path.dirname(manifestPath),
    files: [path.basename(manifestPath)],
  };
}

/**
 * skill: SKILL.md + same-dir leaves (validator.py, CLI.md)
 *   + convention companion subdirs (SKILL_COMPANION_DIRS) + nested sub-skills.
 */
function skillFilter(manifestPath: string): AssetKindFileFilter {
  const dir = path.dirname(manifestPath);
  const manifestName = path.basename(manifestPath);
  const files = [manifestName];

  // Same-dir leaves (from spec "Per-type file filter defaults" table).
  for (const leaf of ["validator.py", "CLI.md"]) {
    if (fs.existsSync(path.join(dir, leaf))) files.push(leaf);
  }

  // Convention companion subdirs (references/, scripts/, modes/, …) — wholesale.
  const companions = new Set(SKILL_COMPANION_DIRS);
  for (const sub of SKILL_COMPANION_DIRS) {
    files.push(...collectDirRecursive(dir, sub));
  }

  // Nested sub-skill directories (*/SKILL.md) not already captured above.
  try {
    for (const entry of fs.readdirSync(dir, { withFileTypes: true })) {
      if (entry.isDirectory() && !companions.has(entry.name)) {
        if (fs.existsSync(path.join(dir, entry.name, "SKILL.md"))) {
          files.push(...collectDirRecursive(dir, entry.name));
        }
      }
    }
  } catch {
    /* empty */
  }

  files.sort();
  return { assetRoot: dir, files };
}

/**
 * agent: agent.yaml or AGENT.md + same-dir SOUL.md, RULES.md
 *   + convention companion subdirs (AGENT_COMPANION_DIRS).
 */
function agentFilter(manifestPath: string): AssetKindFileFilter {
  const dir = path.dirname(manifestPath);
  const manifestName = path.basename(manifestPath);
  const files = [manifestName];

  for (const companion of ["SOUL.md", "RULES.md"]) {
    if (fs.existsSync(path.join(dir, companion))) files.push(companion);
  }

  for (const sub of AGENT_COMPANION_DIRS) {
    files.push(...collectDirRecursive(dir, sub));
  }

  files.sort();
  return { assetRoot: dir, files };
}

/**
 * contract: CONTRACT.md + same-dir attachments (all non-directory entries)
 */
function contractFilter(manifestPath: string): AssetKindFileFilter {
  const dir = path.dirname(manifestPath);
  const manifestName = path.basename(manifestPath);
  const files = [manifestName];
  try {
    for (const entry of fs.readdirSync(dir, { withFileTypes: true })) {
      if (entry.isFile() && entry.name !== manifestName) {
        files.push(entry.name);
      }
    }
  } catch {
    /* empty */
  }
  files.sort();
  return { assetRoot: dir, files };
}

/**
 * connector: CONNECTOR.md + entry file (adapter.ts by default)
 */
function connectorFilter(manifestPath: string): AssetKindFileFilter {
  const dir = path.dirname(manifestPath);
  const manifestName = path.basename(manifestPath);
  const files = [manifestName];
  if (fs.existsSync(path.join(dir, "adapter.ts"))) files.push("adapter.ts");
  files.sort();
  return { assetRoot: dir, files };
}

// ---------------------------------------------------------------------------
// Provider factory
// ---------------------------------------------------------------------------

function makeProvider(config: {
  kind: string;
  displayName: string;
  providerVersion: string;
  discoveryPatterns: string[];
  matchesPath: (relativePath: string) => boolean;
  validate: (data: unknown) => ManifestValidationResult<unknown>;
  fileFilter: (manifestPath: string) => AssetKindFileFilter;
  hasExtractRequires?: boolean;
}): IAssetKindProvider {
  return {
    kind: config.kind,
    displayName: config.displayName,
    providerVersion: config.providerVersion,
    discoveryPatterns: config.discoveryPatterns,
    matchesPath: config.matchesPath,
    validateManifest: config.validate,
    defaultFileFilter: config.fileFilter,
    ...(config.hasExtractRequires !== false && {
      extractRequires: (assetRef: string, manifest: Record<string, unknown>) =>
        extractRequiresFromManifest(assetRef, config.kind, manifest),
    }),
  };
}

// ---------------------------------------------------------------------------
// Core kind providers (8)
// ---------------------------------------------------------------------------

export const skillProvider: IAssetKindProvider = makeProvider({
  kind: "skill",
  displayName: "Skill",
  providerVersion: "1.0.0",
  discoveryPatterns: ["**/SKILL.md"],
  matchesPath: (p) => matchesFilename(p, "SKILL.md"),
  validate: validateSkill,
  fileFilter: skillFilter,
  hasExtractRequires: true,
});

export const agentProvider: IAssetKindProvider = makeProvider({
  kind: "agent",
  displayName: "Agent",
  providerVersion: "1.0.0",
  discoveryPatterns: ["**/agent.yaml", "**/AGENT.md"],
  matchesPath: (p) => matchesFilename(p, "agent.yaml") || matchesFilename(p, "AGENT.md"),
  validate: validateAgent,
  fileFilter: agentFilter,
  hasExtractRequires: true,
});

export const connectorProvider: IAssetKindProvider = makeProvider({
  kind: "connector",
  displayName: "Connector",
  providerVersion: "1.0.0",
  discoveryPatterns: ["**/CONNECTOR.md"],
  matchesPath: (p) => matchesFilename(p, "CONNECTOR.md"),
  validate: validateConnector,
  fileFilter: connectorFilter,
  hasExtractRequires: false,
});

export const mcpServerProvider: IAssetKindProvider = makeProvider({
  kind: "mcp-server",
  displayName: "MCP Server",
  providerVersion: "1.0.0",
  discoveryPatterns: ["**/MCP.md"],
  matchesPath: (p) => matchesFilename(p, "MCP.md"),
  validate: validateMcpServer,
  fileFilter: manifestOnlyFilter,
  hasExtractRequires: false,
});

export const contractProvider: IAssetKindProvider = makeProvider({
  kind: "contract",
  displayName: "Contract",
  providerVersion: "1.0.0",
  discoveryPatterns: ["**/CONTRACT.md"],
  matchesPath: (p) => matchesFilename(p, "CONTRACT.md"),
  validate: validateContract,
  fileFilter: contractFilter,
  hasExtractRequires: false,
});

export const promptProvider: IAssetKindProvider = makeProvider({
  kind: "prompt",
  displayName: "Prompt",
  providerVersion: "1.0.0",
  discoveryPatterns: ["**/*.prompt.md"],
  matchesPath: (p) => matchesSuffix(p, ".prompt.md"),
  validate: validatePrompt,
  fileFilter: manifestOnlyFilter,
  hasExtractRequires: false,
});

/**
 * Persona and Ruleset providers have no FS discovery patterns — they are
 * sourced inline by the agent kind's resolver, not discovered as standalone
 * assets in a source tree. They remain registered as core kinds so the
 * registry recognizes them and can validate their manifests.
 */
export const personaProvider: IAssetKindProvider = {
  kind: "persona",
  displayName: "Persona",
  providerVersion: "1.0.0",
  discoveryPatterns: [],
  matchesPath: () => false,
  validateManifest: validatePersona,
  defaultFileFilter: manifestOnlyFilter,
};

export const rulesetProvider: IAssetKindProvider = {
  kind: "ruleset",
  displayName: "Ruleset",
  providerVersion: "1.0.0",
  discoveryPatterns: [],
  matchesPath: () => false,
  validateManifest: validateRuleset,
  defaultFileFilter: manifestOnlyFilter,
};

// ---------------------------------------------------------------------------
// Extension kind providers
// NOTE: flow lives in @skaile/workspaces/factory-assets/connectors/flow/engine (flowKindProvider).
//       knowledge lives in @skaile/library (knowledgeKindProvider).
//       Both are registered by CLI at bootstrap, not included in BUILTIN_PROVIDERS.
// ---------------------------------------------------------------------------

// ---------------------------------------------------------------------------
// Preset provider (composition entity — discoverable but not in AssetKind enum)
// ---------------------------------------------------------------------------

export const presetProvider: IAssetKindProvider = makeProvider({
  kind: "preset",
  displayName: "Preset",
  providerVersion: "1.0.0",
  discoveryPatterns: ["**/*.preset.yaml"],
  matchesPath: (p) => matchesSuffix(p, ".preset.yaml"),
  validate: validatePreset,
  fileFilter: manifestOnlyFilter,
  hasExtractRequires: true,
});

// ---------------------------------------------------------------------------
// All built-in providers
// ---------------------------------------------------------------------------

/**
 * Built-in providers shipped with @skaile/discovery.
 *
 * 8 core kinds + preset. Extension kinds (flow, knowledge) moved to their
 * owning packages and are registered by the CLI at bootstrap.
 */
export const BUILTIN_PROVIDERS: IAssetKindProvider[] = [
  // 8 core kinds
  skillProvider,
  agentProvider,
  connectorProvider,
  mcpServerProvider,
  contractProvider,
  promptProvider,
  personaProvider,
  rulesetProvider,
  // Composition entity (discoverable, not in AssetKind enum)
  presetProvider,
];
